/**
 * Custom Discount Modal Component
 * Phase 3 Frontend - Create and edit custom discounts
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppLocalizationProvider } from '../../common/AppLocalizationProvider';
import {
  useCreateCustomDiscount,
  useUpdateCustomDiscount,
  useVariableDiscountSettings,
} from '../../../hooks/useDiscounts';
import { toLocalDateString, localDateFromKey } from '../../../utils/dateUtils';
import {
  COVERS_EVERYTHING,
  coverageFromRow,
  coverageFromSchoolDefault,
  coverageIsEmpty,
  normaliseCoverage,
} from '../../../utils/discountCoverage';
import DiscountCoverageFields from './DiscountCoverageFields';
import {
  CustomDiscount,
  CreateCustomDiscountInput,
  UpdateCustomDiscountInput,
  DiscountScope,
  DiscountStatus,
} from '../../../types/discount.types';

/** The create form's starting values, a fresh object each time. */
const emptyForm = (): CreateCustomDiscountInput => ({
  name: '',
  description: '',
  scope: DiscountScope.FAMILY,
  value: 0,
  isPercent: true,
  isStackable: true,
  status: DiscountStatus.ACTIVE,
  applicableFamilies: [],
  applicableStudents: [],
  maxUsagePerFamily: undefined,
  maxUsagePerStudent: undefined,
  maxUsageTotal: undefined,
  startDate: null,
  endDate: null,
  coverage: { ...COVERS_EVERYTHING, coversCategoryIds: [] },
});

interface CustomDiscountModalProps {
  open: boolean;
  onClose: () => void;
  discount?: CustomDiscount | null;
  onSuccess?: () => void;
}

export default function CustomDiscountModal({
  open,
  onClose,
  discount,
  onSuccess,
}: CustomDiscountModalProps) {
  const isEdit = !!discount?.id;
  const createMutation = useCreateCustomDiscount();
  const updateMutation = useUpdateCustomDiscount();
  const [dirty, setDirty] = useState(false);
  // #625 D6: a NEW discount starts from the school default (Payment Settings → Discounts).
  const { data: settings, isSuccess: settingsLoaded } = useVariableDiscountSettings({ enabled: open });
  const schoolDefault = settingsLoaded ? coverageFromSchoolDefault(settings) : undefined;
  // #625: the coverage is sent ONLY when the admin changed it (/qa phase 5 M1, L2). Untouched, a
  // new entry takes the school default on the server (never a placeholder sent before the default
  // loaded) and an edit leaves the stored coverage as it is.
  const [coverageTouched, setCoverageTouched] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateCustomDiscountInput>({ defaultValues: emptyForm() });

  // Update form when discount changes
  useEffect(() => {
    if (discount) {
      reset({
        name: discount.name,
        description: discount.description || '',
        scope: discount.scope,
        value: discount.value,
        isPercent: discount.isPercent,
        isStackable: discount.isStackable,
        status: discount.status,
        applicableFamilies: discount.applicableFamilies,
        applicableStudents: discount.applicableStudents,
        maxUsagePerFamily: discount.maxUsagePerFamily || undefined,
        maxUsagePerStudent: discount.maxUsagePerStudent || undefined,
        maxUsageTotal: discount.maxUsageTotal || undefined,
        // Rebuild at LOCAL midnight from the stored calendar day, mirroring the write in
        // `onSubmit`. A plain parse of a UTC-midnight `@db.Date` shows the previous day west
        // of UTC, and the picker would then save that shifted day back.
        startDate: discount.startDate ? localDateFromKey(discount.startDate) : discount.startDate,
        endDate: discount.endDate ? localDateFromKey(discount.endDate) : discount.endDate,
        // #625: what it covers, as stored (D4). Saved back whole with the edit.
        coverage: coverageFromRow(discount),
      });
      setDirty(false);
    } else {
      // A fresh empty form: a bare reset() restores the values of the LAST reset, which were the
      // entry edited before, so "Create Discount" opened pre-filled with it (/qa phase 5).
      reset(emptyForm());
      setDirty(false);
    }
    setCoverageTouched(false);
  }, [discount, open, reset]);

  // A new discount takes the school default once the settings arrive, unless the admin has
  // already changed anything (then their choice stands).
  useEffect(() => {
    if (!isEdit && open && schoolDefault && !coverageTouched) {
      setValue('coverage', schoolDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- schoolDefault is rebuilt per render
  }, [isEdit, open, settingsLoaded, settings]);

  const isPercent = watch('isPercent');

  const handleClose = () => {
    if (dirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) return;
    }
    onClose();
  };

  const onSubmit = async (data: CreateCustomDiscountInput) => {
    try {
      // `startDate`/`endDate` are CALENDAR DAYS (`@db.Date`). Left as Date objects, axios
      // serialises them via `Date.prototype.toJSON` (= `toISOString`) — which both 400'd
      // against the `z.date()` validator AND, once coerced, would have stored the previous
      // day for an admin east of UTC. Send the admin's local calendar day as a bare key.
      const { coverage, ...rest } = data;
      const payload = {
        ...rest,
        ...(coverageTouched && coverage ? { coverage: normaliseCoverage(coverage) } : {}),
        startDate: data.startDate ? toLocalDateString(new Date(data.startDate)) : data.startDate,
        endDate: data.endDate ? toLocalDateString(new Date(data.endDate)) : data.endDate,
      } as unknown as CreateCustomDiscountInput;

      if (isEdit && discount) {
        await updateMutation.mutateAsync({
          id: discount.id,
          data: payload as UpdateCustomDiscountInput,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by hook snackbar
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Discount' : 'Create Discount'}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <AppLocalizationProvider>
          <Stack spacing={3}>
          {/* Name Field */}
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Discount Name"
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name?.message}
                onChange={(e) => {
                  field.onChange(e);
                  setDirty(true);
                }}
              />
            )}
          />

          {/* Description Field */}
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                rows={2}
                placeholder="What is this discount for?"
                onChange={(e) => {
                  field.onChange(e);
                  setDirty(true);
                }}
              />
            )}
          />

          {/* Scope Selection */}
          <Controller
            name="scope"
            control={control}
            rules={{ required: 'Scope is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.scope}>
                <InputLabel>Scope</InputLabel>
                <Select
                  {...field}
                  label="Scope"
                  onChange={(e) => {
                    field.onChange(e);
                    setDirty(true);
                  }}
                >
                  <MenuItem value={DiscountScope.FAMILY}>Family</MenuItem>
                  <MenuItem value={DiscountScope.STUDENT}>Student</MenuItem>
                  {/* #625 D8: retired for new discounts (what it covers decides the lines); an
                      existing one keeps it, and the server refuses changing another to it. */}
                  {discount?.scope === DiscountScope.INVOICE_ITEM && (
                    <MenuItem value={DiscountScope.INVOICE_ITEM}>Invoice Item</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          />

          {/* Percentage vs Fixed Toggle */}
          <FormControlLabel
            control={
              <Controller
                name="isPercent"
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      setDirty(true);
                    }}
                  />
                )}
              />
            }
            label={
              <Typography variant="body2">
                {isPercent
                  ? 'Percentage Discount'
                  : 'Fixed Dollar Amount Discount'}
              </Typography>
            }
          />

          {/* Value Input */}
          <Controller
            name="value"
            control={control}
            rules={{
              required: 'Value is required',
              min: { value: 0, message: 'Must be 0 or greater' },
              max: isPercent
                ? { value: 100, message: 'Percentage must be 100 or less' }
                : undefined,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Discount Value"
                type="number"
                fullWidth
                required
                error={!!errors.value}
                helperText={errors.value?.message}
                InputProps={{
                  startAdornment: !isPercent && (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  endAdornment: isPercent && (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  field.onChange(e);
                  setDirty(true);
                }}
              />
            )}
          />

          {/* What it covers (#625) */}
          <Controller
            name="coverage"
            control={control}
            rules={{
              validate: (v) => !v || !coverageIsEmpty(v) || 'Choose at least one kind of line the discount covers.',
            }}
            render={({ field }) => (
              <DiscountCoverageFields
                value={field.value ?? { ...COVERS_EVERYTHING, coversCategoryIds: [] }}
                defaultCoverage={schoolDefault}
                onChange={(next) => {
                  field.onChange(next);
                  setCoverageTouched(true);
                  setDirty(true);
                }}
              />
            )}
          />

          {/* Status */}
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  {...field}
                  label="Status"
                  onChange={(e) => {
                    field.onChange(e);
                    setDirty(true);
                  }}
                >
                  <MenuItem value={DiscountStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={DiscountStatus.INACTIVE}>Inactive</MenuItem>
                  <MenuItem value={DiscountStatus.ARCHIVED}>Archived</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {/* Stacking Toggle */}
          <FormControlLabel
            control={
              <Controller
                name="isStackable"
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      setDirty(true);
                    }}
                  />
                )}
              />
            }
            label="Can be combined with other discounts"
          />

          {/* Usage Limits */}
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Usage Limits (Optional)
            </Typography>

            <Stack spacing={2} sx={{ mt: 1 }}>
              <Controller
                name="maxUsagePerFamily"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Max Uses Per Family"
                    type="number"
                    size="small"
                    placeholder="Leave empty for unlimited"
                    onChange={(e) => {
                      field.onChange(e.target.value ? parseInt(e.target.value) : null);
                      setDirty(true);
                    }}
                  />
                )}
              />

              <Controller
                name="maxUsagePerStudent"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Max Uses Per Student"
                    type="number"
                    size="small"
                    placeholder="Leave empty for unlimited"
                    onChange={(e) => {
                      field.onChange(e.target.value ? parseInt(e.target.value) : null);
                      setDirty(true);
                    }}
                  />
                )}
              />

              <Controller
                name="maxUsageTotal"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Max Total Uses"
                    type="number"
                    size="small"
                    placeholder="Leave empty for unlimited"
                    onChange={(e) => {
                      field.onChange(e.target.value ? parseInt(e.target.value) : null);
                      setDirty(true);
                    }}
                  />
                )}
              />
            </Stack>
          </Box>

          {/* Date Range */}
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Valid Date Range (Optional)
            </Typography>

            <Stack spacing={2} sx={{ mt: 1 }}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Start Date"
                    value={field.value instanceof Date ? field.value : field.value ? new Date(field.value) : null}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' },
                    }}
                    onChange={(date) => {
                      field.onChange(date);
                      setDirty(true);
                    }}
                  />
                )}
              />

              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="End Date"
                    value={field.value instanceof Date ? field.value : field.value ? new Date(field.value) : null}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' },
                    }}
                    onChange={(date) => {
                      field.onChange(date);
                      setDirty(true);
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        </Stack>
        </AppLocalizationProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!dirty || isPending}
          startIcon={isPending && <CircularProgress size={20} />}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
